import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Navbar,
  Nav,
  Button,
  Spinner,
  Pagination,
  Badge,
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  User,
  authService,
  friendshipService,
  notificationService,
  NotificationItem,
  loadUnreadNotificationCount,
} from '../services/api';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import PwaInstallButton from './PwaInstallButton';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import { formatNotificationTimestamp } from '../utils/formatNotificationTimestamp';
import './UserProfile.css';
import './NotificationsPage.css';

const PAGE_SIZE = 15;

const NotificationsPage: React.FC = () => {
  const { t, i18n } = useSafeTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);

  const loadList = useCallback(async (p: number) => {
    setListLoading(true);
    try {
      const data = await notificationService.getNotifications({
        page: p,
        pageSize: PAGE_SIZE,
      });
      setItems(data.items);
      setTotalCount(data.totalCount);
      setTotalPages(Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE)));
    } catch {
      setItems([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      if (token) {
        localStorage.setItem('authToken', token);
      }
      try {
        const userData = await authService.getProfile();
        setUser(userData);
        const unread = await loadUnreadNotificationCount(userData);
        setUnreadNotificationCount(unread);
      } catch {
        window.location.href = '/';
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [token]);

  useEffect(() => {
    if (user) {
      void loadList(page);
    }
  }, [user, page, loadList]);

  useEffect(() => {
    if (user) {
      void friendshipService
        .getPendingRequests()
        .then((r) => setPendingRequestsCount(r.length))
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    window.location.href = '/';
  };

  const handleRequestResponded = () => {
    void friendshipService
      .getPendingRequests()
      .then((r) => setPendingRequestsCount(r.length))
      .catch(() => {});
  };

  const refreshUnread = async () => {
    if (!user) return;
    const n = await loadUnreadNotificationCount(user);
    setUnreadNotificationCount(n);
  };

  const handleMarkRead = async (n: NotificationItem) => {
    if (n.readAt) return;
    try {
      await notificationService.markRead(n.id);
      setItems((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x))
      );
      await refreshUnread();
    } catch {
      /* ignore */
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await notificationService.markAllRead();
      setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt ?? new Date().toISOString() })));
      setUnreadNotificationCount(0);
    } catch {
      /* ignore */
    } finally {
      setMarkingAll(false);
    }
  };

  if (isLoading) {
    return (
      <div className="profile-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="profile-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to="/"
            className="navbar-brand d-flex align-items-center"
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            <NotificationBell
              className="d-lg-none"
              unreadCount={unreadNotificationCount}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
                {!user && <NavbarMusicToggle />}
              </div>
              <UserNavbarMenus
                user={user}
                pendingRequestsCount={pendingRequestsCount}
                unreadNotificationCount={unreadNotificationCount}
                onUnreadNotificationCountChange={setUnreadNotificationCount}
                onOpenFriends={() => setShowFriendsList(true)}
                onOpenPending={() => setShowPendingRequests(true)}
                onLogout={handleLogout}
              />
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content">
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={8}>
            <div className="notifications-page-header mb-3">
              <h1 className="h3 mb-1">{t('notifications.pageTitle', 'Notifications')}</h1>
              <p className="text-muted small mb-0">
                {t('notifications.pageSubtitle', 'Your recent alerts and messages.')}
              </p>
            </div>
            <Card className="profile-card mb-3">
              <Card.Body className="d-flex flex-wrap gap-2 justify-content-between align-items-center py-3">
                <span className="text-muted small">
                  {totalCount}{' '}
                  {totalCount === 1
                    ? t('notifications.totalSingular', 'notification')
                    : t('notifications.totalPlural', 'notifications')}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={markingAll || unreadNotificationCount === 0}
                  onClick={() => void handleMarkAllRead()}
                >
                  {markingAll ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('common.loading', 'Loading...')}
                    </>
                  ) : (
                    t('notifications.markAllRead', 'Mark all as read')
                  )}
                </Button>
              </Card.Body>
            </Card>

            <Card className="profile-card">
              <Card.Body className="p-0">
                {listLoading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" />
                  </div>
                )}
                {!listLoading && items.length === 0 && (
                  <p className="text-muted text-center py-5 mb-0">
                    {t('notifications.emptyList', 'No notifications yet.')}
                  </p>
                )}
                {!listLoading &&
                  items.map((n) => (
                    <div
                      key={n.id}
                      className={`notifications-list-item px-3 py-3 border-bottom ${!n.readAt ? 'notifications-list-item-unread' : ''}`}
                    >
                      <div className="d-flex justify-content-between gap-2 align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <strong className="notifications-list-title">{n.title}</strong>
                            {!n.readAt && (
                              <Badge bg="primary" pill>
                                {t('notifications.new', 'New')}
                              </Badge>
                            )}
                          </div>
                          {n.body && <p className="mb-1 mt-1 text-muted small">{n.body}</p>}
                          <div className="notifications-list-meta small text-muted">
                            {n.category} ·{' '}
                            {formatNotificationTimestamp(n.createdAt, i18n.language, n.category)}
                          </div>
                        </div>
                        {!n.readAt && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-nowrap flex-shrink-0"
                            onClick={() => void handleMarkRead(n)}
                          >
                            {t('notifications.markRead', 'Mark read')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
              </Card.Body>
            </Card>

            {totalPages > 1 && (
              <div className="d-flex justify-content-center my-4">
                <Pagination>
                  <Pagination.Prev
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  />
                  <Pagination.Item active>{page}</Pagination.Item>
                  <Pagination.Next
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  />
                </Pagination>
              </div>
            )}

            <Card className="profile-card mt-3 back-to-home-card">
              <Card.Body className="d-flex justify-content-center py-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  onClick={() => navigate('/')}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  {t('navigation.backToHome', 'Back to Home')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}

      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={handleRequestResponded}
        />
      )}
    </div>
  );
};

export default NotificationsPage;
